import { ShotTechniqueEnum } from '../../utils/shotTechniqueEnum';
import { EventShotLightVm } from '../../utils/api';

/*
 * Shot attributes orthogonal to Situation (6v6 / EP / Pen).
 * From EventShot: ShotTechnique, IsDirectFromFoul, FakeCount.
 * Null technique = Direct (legacy rows + match-log default).
 * FakeCount is 0 / 1 / 2 in the match log (1 fake vs "more fakes").
 */

export type ShotTechniqueKey = 'direct' | 'lob' | 'skip';

export const SHOT_TECHNIQUE_KEYS: ShotTechniqueKey[] = ['direct', 'lob', 'skip'];

export const SHOT_TECHNIQUE_LABELS: Record<ShotTechniqueKey, string> = {
    direct: 'Direct',
    lob: 'Lob',
    skip: 'Skip',
};

export type ShotFoulKey = 'foul' | 'open';

export const SHOT_FOUL_KEYS: ShotFoulKey[] = ['foul', 'open'];

export const SHOT_FOUL_LABELS: Record<ShotFoulKey, string> = {
    foul: 'Direct foul',
    open: 'Not from foul',
};

export type ShotFakeKey = 'none' | 'one' | 'more';

export const SHOT_FAKE_KEYS: ShotFakeKey[] = ['none', 'one', 'more'];

export const SHOT_FAKE_LABELS: Record<ShotFakeKey, string> = {
    none: 'No fake',
    one: '1 fake',
    more: '2+ fakes',
};

/** null = all; [] = none; otherwise selected keys. Same contract as Period. */
export interface ShotKindFilter {
    techniques?: ShotTechniqueKey[] | null;
    fouls?: ShotFoulKey[] | null;
    fakes?: ShotFakeKey[] | null;
}

export const techniqueKeyOf = (shot: EventShotLightVm): ShotTechniqueKey => {
    if (shot.shotTechnique === ShotTechniqueEnum.Lob) return 'lob';
    if (shot.shotTechnique === ShotTechniqueEnum.Skip) return 'skip';
    return 'direct';
};

export const fakeKeyOf = (shot: EventShotLightVm): ShotFakeKey => {
    const n = shot.fakeCount ?? 0;
    if (n <= 0) return 'none';
    if (n === 1) return 'one';
    return 'more';
};

const allows = <T>(selected: T[] | null | undefined, value: T): boolean => {
    if (selected == null) return true;
    if (selected.length === 0) return false;
    return selected.includes(value);
};

export const matchesShotKind = (shot: EventShotLightVm, kind?: ShotKindFilter): boolean => {
    if (!kind) return true;
    if (!allows(kind.techniques, techniqueKeyOf(shot))) return false;
    if (!allows(kind.fouls, shot.isDirectFromFoul ? 'foul' : 'open')) return false;
    if (!allows(kind.fakes, fakeKeyOf(shot))) return false;
    return true;
};

const dimensionLabel = <T extends string>(
    selected: T[] | null | undefined,
    labels: Record<T, string>
): string | undefined => {
    if (selected == null || selected.length === 0) return undefined;
    const allKeys = Object.keys(labels) as T[];
    if (selected.length === allKeys.length) return undefined;
    return selected.map((key) => labels[key]).join(' + ');
};

/** Extra summary bits when a kind dimension is narrowed. Empty string if all-open. */
export const shotKindFilterLabel = (kind?: ShotKindFilter): string => {
    if (!kind) return '';
    return [
        dimensionLabel(kind.techniques, SHOT_TECHNIQUE_LABELS),
        dimensionLabel(kind.fouls, SHOT_FOUL_LABELS),
        dimensionLabel(kind.fakes, SHOT_FAKE_LABELS),
    ]
        .filter(Boolean)
        .join(' · ');
};
